import React, { FC, lazy, Suspense, useContext, useState } from 'react'
import LoadingState from 'modules/shares/components/LoadingState'
import { RouteParams } from 'constants/routes'
import {
  useGetPagePresentationFileQuery,
  usePresentationFileSubscription,
  PresentationFileFragment,
  PresentationFileFragmentDoc,
} from 'gqlTypes'
import { ShareVersionContext } from 'modules/versioning'
import { useParams } from 'react-router'
import ErrorMessage from 'components/ErrorMessage'
import config from 'config'
import { RendererStatus } from '@sketch-hq/sketch-web-renderer'
import { ReactComponent as DocumentProcessing } from 'icons/document-processing-64.svg'
import { getRenderStatus, RenderStatus } from 'utils/renderStatus'
import { getErrorDescription, getLoadingMessage } from './utils'

import {
  StyledPageCanvas,
  StyledWebRendererStatusContainer,
  StyledWebRendererFeaturePreviewContainer,
  StyledBackButton,
} from './PageCanvas.styles'
import { PageCanvasError } from 'modules/shares/PageCanvasView/components/PageCanvasError'
import { PageCanvasFeaturePreview } from 'modules/shares/PageCanvasView/components/PageCanvasFeaturePreview'
import { dataIdFromObject } from 'graphql/cache'

const WebRenderer = lazy(() =>
  import('modules/shares/components/WebRenderer').then(module => ({
    default: module.WebRenderer,
  }))
)

const PageCanvasProcessing: FC = () => (
  <ErrorMessage
    title="Rendering Canvas…"
    description="The canvas is still rendering. When it’s ready, it will appear here."
    icon={<DocumentProcessing />}
  />
)

type WebRendererStatusProps = {
  rendererStatus?: RendererStatus
}

const WebRendererStatus: FC<WebRendererStatusProps> = props => {
  const { rendererStatus } = props

  if (
    // rendererStatus state is undefined by default and
    // gets updated to 'INIT' only after the web renderer
    // was created.
    !rendererStatus ||
    rendererStatus.type === 'INIT' ||
    rendererStatus.type === 'LOADING_FILE' ||
    rendererStatus.type === 'FILE_READY' ||
    rendererStatus.type === 'DRAWING_FILE'
  ) {
    return <LoadingState message={getLoadingMessage(rendererStatus)} />
  }

  if (
    rendererStatus.type === 'FAILURE' ||
    rendererStatus.type === 'WEBGL_CONTEXT_LOST'
  ) {
    return (
      <PageCanvasError
        showReloadCanvasAction
        showViewAsArtboardsAction
        description={getErrorDescription(rendererStatus)}
      />
    )
  }

  return null
}

export const PageCanvas: FC = () => {
  const { shareID, pageUUID } = useParams<
    RouteParams<'SHARE_PAGE_CANVAS_VIEW'>
  >()
  const shareVersionContext = useContext(ShareVersionContext)!
  const { versionShortId } = shareVersionContext
  const [rendererStatus, setRendererStatus] = useState<
    RendererStatus | undefined
  >()

  const imagesURLFormat = `${config.web_renderer_image_domain}/presentation/p/${pageUUID}/i/:imageId`
  const documentRenderStatus = !shareVersionContext.loading
    ? getRenderStatus(
        shareVersionContext.currentVersion.document?.renderStatus ?? null
      )
    : null

  const prFileQuery = useGetPagePresentationFileQuery({
    variables: {
      pageUUID,
      share: shareID,
      version: versionShortId,
    },
    // Hold the PR file request until the document is marked as
    // ready on the backend.
    skip: documentRenderStatus !== 'ready',
  })
  const presentationFile =
    prFileQuery.data?.share?.version?.document?.page?.presentationFile
  const pageIdentifier =
    prFileQuery.data?.share?.version?.document?.page?.identifier

  usePrFileStatusChangesSubscriptionIfNeeded(
    documentRenderStatus,
    presentationFile,
    pageIdentifier
  )

  if (shareVersionContext.loading || prFileQuery.loading) {
    return <LoadingState />
  }

  const downloadUrl = presentationFile?.downloadUrl

  if (
    documentRenderStatus !== 'ready' ||
    presentationFile?.status === 'PENDING'
  ) {
    return <PageCanvasProcessing />
  }

  /**
   * @TODO This conditional can be safely removed post MVP when we remove the
   * web-canvas-mvp feature flag in https://github.com/sketch-hq/Cloud/issues/9839
   */
  if (downloadUrl?.includes('.json')) {
    return (
      <PageCanvasError
        description={
          <>
            Please save a new document version in Sketch to see this page as a
            canvas. This is a temporary error condition only applicable to
            internal Sketch users using the canvas during the MVP.
          </>
        }
        showReloadCanvasAction
      />
    )
  }

  if (prFileQuery.error) {
    return (
      <PageCanvasError
        description={
          <>
            There was a problem fetching the page data. You can find more
            information on our{' '}
            <a href="https://status.sketch.com/">Status Page</a>.
          </>
        }
        showReloadCanvasAction
        showViewAsArtboardsAction
      />
    )
  }

  return (
    <StyledPageCanvas>
      <Suspense fallback={<LoadingState />}>
        <WebRenderer
          downloadUrl={downloadUrl!}
          imagesURLFormat={imagesURLFormat}
          onStatus={setRendererStatus}
        />
      </Suspense>
      <StyledBackButton />
      {rendererStatus?.type === 'READY' && (
        <StyledWebRendererFeaturePreviewContainer>
          <PageCanvasFeaturePreview />
        </StyledWebRendererFeaturePreviewContainer>
      )}
      {rendererStatus?.type !== 'READY' && (
        <StyledWebRendererStatusContainer data-testid="loading-canvas-overlay">
          <WebRendererStatus rendererStatus={rendererStatus} />
        </StyledWebRendererStatusContainer>
      )}
    </StyledPageCanvas>
  )
}

/**
 * For users with the web-canvas-mvp FF, the presentation file is generated at the same time
 * as the document and its assets just after the user updated it in the sketch app. For other users,
 * we don't generate the presentation file automatically when a new version of the document is created,
 * so cloud users may try to open a canvas for which the presentation file has not yet been generated.
 *
 * Sending the getPagePresentationFile query sends to the server a request to get the presentation file.
 * If the presentation file is ready then we can use it straight away and we don't need this subscription.
 * If not available yet, the backend starts generating the presentation file and this subscription allows
 * us to receive an event to update the frontend when the Presentation file is ready.
 */
function usePrFileStatusChangesSubscriptionIfNeeded(
  documentRenderStatus: RenderStatus | null,
  presentationFile: PresentationFileFragment['presentationFile'] | undefined,
  pageIdentifier: string | undefined
) {
  const shouldSubscribeToPrFileStatusChanges =
    Boolean(pageIdentifier) &&
    documentRenderStatus === 'ready' &&
    presentationFile?.status === 'PENDING'

  usePresentationFileSubscription({
    variables: { pageId: pageIdentifier ?? '' },
    onSubscriptionData: ({ client, subscriptionData }) => {
      const pageCacheId = dataIdFromObject({
        __typename: 'Page',
        identifier: pageIdentifier,
      })

      const newDownloadUrl = subscriptionData.data?.presentationFile.url

      if (pageCacheId && newDownloadUrl) {
        client.writeFragment<PresentationFileFragment>({
          fragment: PresentationFileFragmentDoc,
          id: pageCacheId,
          data: {
            __typename: 'Page',
            presentationFile: {
              __typename: 'PresentationFile',
              downloadUrl: newDownloadUrl,
              status: 'COMPLETED',
            },
          },
        })
      }
    },
    skip: !shouldSubscribeToPrFileStatusChanges,
  })
}
